// What compose starts with (R-M6): an empty letter with the signature, a reply with the quote, reply-all, a forward with the files,
// or a draft taken back from «Черновики». The text is markdown of the block editor (decision 034): a quoted letter is a quote block.

import { plainToMarkdown, quoteMarkdown, uuidv7, type Id } from '@confdeck/core-shared';
import { bodyOf, fullTime, people, type Attachment, type Letter } from './model.ts';
import type { Mailbox } from './use-letters.ts';

export type ReplyMode = 'reply' | 'replyAll' | 'forward';

export interface Draft {
  id: Id; mailboxId: Id; to: string; cc: string; bcc: string; subject: string; text: string;
  replyTo: Id | null; attachments: Attachment[]; saved: boolean;
}

/** The signature is a long text of the mailbox — already markdown; «--» stands on its own line above it. */
const sig = (signature: string) => (signature.trim() ? `--\n\n${signature.trim()}` : '');

export const newDraft = (mailboxId: Id, signature = ''): Draft => ({ id: uuidv7(), mailboxId, to: '', cc: '', bcc: '', subject: '', text: sig(signature), replyTo: null, attachments: [], saved: false });

const prefixed = (prefix: string, subject: string) => (new RegExp(`^${prefix}:`, 'i').test(subject.trim()) ? subject.trim() : `${prefix}: ${subject.trim()}`);
const address = (p: { name: string; address: string }) => (p.name && p.name !== p.address ? `${p.name} <${p.address}>` : p.address);

export async function draftFrom(l: Letter, mode?: ReplyMode, mb?: Mailbox): Promise<Draft> {
  const body = await bodyOf(l.id).catch(() => ({ text: l.snippet, html: null, markdown: null }));
  // A letter written here keeps its blocks; one read from a mailbox is plain text.
  const md = body.markdown ?? plainToMarkdown(body.text);
  if (!mode) {
    // A draft from «Черновики»: the same letter continues.
    return { id: l.id, mailboxId: l.mailboxId ?? mb?.id ?? '', to: l.to, cc: l.cc, bcc: String(l.rec.data.bcc ?? ''), subject: l.subject, text: md, replyTo: null, attachments: l.attachments, saved: true };
  }
  const own = mb?.address.toLowerCase() ?? '';
  const from = { name: l.fromName, address: l.from };
  const head = `${fullTime(l.date)}, ${l.fromName || l.from}${l.fromName ? ` <${l.from}>` : ''} пишет:`;
  const base = newDraft(mb?.id ?? l.mailboxId ?? '', mb?.signature ?? '');
  if (mode === 'forward') {
    const head = plainToMarkdown(['---------- Пересланное письмо ----------', `От: ${l.fromName ? `${l.fromName} <${l.from}>` : l.from}`, `Дата: ${fullTime(l.date)}`, `Тема: ${l.subject}`, `Кому: ${l.to}`].join('\n'));
    return { ...base, subject: prefixed('Fwd', l.subject), text: [base.text, head, md].filter(Boolean).join('\n\n'), attachments: l.attachments.filter((a) => a.url) };
  }
  // A reply to our own letter goes to the people it was sent to.
  const mine = l.folder === 'sent' || l.from.toLowerCase() === own;
  const to = mine ? people(l.to) : [from];
  const cc = mode === 'replyAll' ? [...(mine ? [] : people(l.to)), ...people(l.cc)].filter((p) => p.address.toLowerCase() !== own && !to.some((t) => t.address === p.address)) : [];
  return { ...base, to: to.map(address).join(', '), cc: cc.map(address).join(', '), subject: prefixed('Re', l.subject), text: [base.text, plainToMarkdown(head), quoteMarkdown(body.text)].filter(Boolean).join('\n\n'), replyTo: l.id };
}

/** What the server gets of a draft: the letter's own fields — the «saved» flag is not one of them (review P3). */
export const draftInput = (x: Draft) => ({ id: x.id, mailboxId: x.mailboxId, to: x.to, cc: x.cc, bcc: x.bcc, subject: x.subject, text: x.text, replyTo: x.replyTo, attachments: x.attachments });

/** The draft differs from its last save; an empty new letter has nothing to keep. */
export function draftUnsaved(x: Draft, savedSig: string): boolean {
  if (JSON.stringify(draftInput(x)) === savedSig) return false;
  return x.saved || !!(x.to.trim() || x.subject.trim() || x.text.trim() || x.attachments.length);
}

/**
 * Closing the compose window saves the draft first; a failed save keeps the window with its text and the reason (audit 31.10,
 * P2-5) — closing would drop the only copy of the letter.
 */
export async function closeWhenSaved(save: () => Promise<boolean>, close: () => void): Promise<boolean> {
  if (!(await save())) return false;
  close();
  return true;
}
